import ChatPaneV2 from './ChatPaneV2';
import { readChatSessionIdentity, useAuthSessionRevision } from 'src/auth/sessionEvents';
// SupportInbox (issue #423). Admin-only inbox with status tabs, claim/
// reassign/set-status row actions, and a deep-link to the support thread
// view. The actual in-thread internal-notes UI lives in
// `SupportThreadComposer.tsx` (rendered from the main Messenger pane when
// `chat.type === 'SUPPORT'`).
//
// Authorization: visible only when the session role is in the admin set.
// The backend is the authoritative gate; this is a UX guard so we don't
// bait non-admin sessions into a 403.

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Avatar,
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
  styled,
} from '@mui/material';
import {
  ChatBubbleOutline,
  OpenInNew,
  PersonAdd,
  SwapHoriz,
} from '@mui/icons-material';
import moment from 'moment';
import { ChatSocketProvider } from 'src/realtime/ChatSocketProvider';
import { useChatEventRouter } from 'src/realtime/useChatEventRouter';

import Label from 'src/components/Label';
import { config } from 'src/config';
import {
  getPlatformSupportTicket,
} from 'src/queries/chat/api';
import {
  useAssignSupport,
  useSupportInbox,
  useUpdateSupportStatus,
} from 'src/queries/chat';

import type { Chat, SupportStatus } from 'src/models/chat';

const STATUS_OPTIONS: SupportStatus[] = [
  'OPEN',
  'ASSIGNED',
  'WAITING_ON_CONTRACTOR',
  'WAITING_ON_ADMIN',
  'RESOLVED',
  'CLOSED',
];

type TabKey =
  | 'open'
  | 'mine'
  | 'waiting-contractor'
  | 'waiting-admin'
  | 'resolved'
  | 'closed';

const TAB_DEFS: Array<{ key: TabKey; label: string }> = [
  { key: 'open', label: 'Open' },
  { key: 'mine', label: 'Assigned to me' },
  { key: 'waiting-contractor', label: 'Waiting on contractor' },
  { key: 'waiting-admin', label: 'Waiting on admin' },
  { key: 'resolved', label: 'Resolved' },
  { key: 'closed', label: 'Closed' },
];

const Wrapper = styled(Box)(
  ({ theme }) => `
    padding: ${theme.spacing(3)};
  `
);

function readSession(): { id: number; roleName?: string } | undefined {
  try {
    const raw = localStorage.getItem('PLS');
    if (!raw) return undefined;
    const parsed = JSON.parse(raw);
    const user = parsed?.user ?? parsed;
    const id = Number(user?.id);
    if (!Number.isFinite(id)) return undefined;
    const roleName = user?.Role?.name as string | undefined;
    return { id, roleName };
  } catch {
    return undefined;
  }
}

function filterRows(
  rows: Chat[] | undefined,
  tab: TabKey,
  currentUserId: number
): Chat[] {
  if (!rows) return [];
  switch (tab) {
    case 'open':
      return rows.filter(
        (r) =>
          (r.supportStatus === 'OPEN' || !r.supportStatus) &&
          !r.assignedAdminId
      );
    case 'mine':
      return rows.filter(
        (r) =>
          r.assignedAdminId === currentUserId &&
          r.supportStatus !== 'RESOLVED' &&
          r.supportStatus !== 'CLOSED'
      );
    case 'waiting-contractor':
      return rows.filter((r) => r.supportStatus === 'WAITING_ON_CONTRACTOR');
    case 'waiting-admin':
      return rows.filter((r) => r.supportStatus === 'WAITING_ON_ADMIN');
    case 'resolved':
      return rows.filter((r) => r.supportStatus === 'RESOLVED');
    case 'closed':
      return rows.filter((r) => r.supportStatus === 'CLOSED');
    default:
      return rows;
  }
}

function describeRequester(row: Chat): {
  name: string;
  avatar: string | undefined;
} {
  // The Chat row from `/chats/support` carries `participants` for the
  // requester. Fall back to a stub if absent.
  const requester =
    row.participants?.find((p) => p.role === 'CONTRACTOR') ??
    row.participants?.[0];
  const u = requester?.user;
  if (!u) return { name: 'Unknown contractor', avatar: undefined };
  const name = `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim();
  const avatar = u.profileImage
    ? (config.FILe_URL ?? '') + u.profileImage
    : undefined;
  return { name: name || 'Contractor', avatar };
}

function SupportInboxInner() {
  const navigate = useNavigate();
  const location = useLocation();
  const session = readSession();
  const isAdmin = session?.roleName === 'SUPER_ADMIN';
  useAuthSessionRevision();
  const isPlatform = location.pathname.startsWith('/platform/');
  const supportScope = isPlatform ? 'platform' : 'legacy';
  const pageTitle = isPlatform ? 'Support Tickets' : 'Support Inbox';

  // Always mount the router so realtime updates flow even when the user
  // sits on the inbox view.
  useChatEventRouter({ role: session?.roleName });

  const [tab, setTab] = useState<TabKey>('open');
  const [reassignChat, setReassignChat] = useState<Chat | null>(null);
  const [reassignAdminId, setReassignAdminId] = useState<string>('');
  const [viewChat, setViewChat] = useState<Chat | null>(null);

  // Server-side filtering: the controller only supports `?status=`. We
  // fetch the full inbox once and slice client-side; the inbox is small
  // enough that this is cheaper than per-tab refetches.
  const { data: rows, isFetching } = useSupportInbox(
    undefined,
    { enabled: isAdmin },
    supportScope
  );
  const assignMutation = useAssignSupport(supportScope);
  const statusMutation = useUpdateSupportStatus(supportScope);
  const ticketDetail = useQuery({
    queryKey: ['platform-support-ticket', readChatSessionIdentity(), viewChat?.id],
    queryFn: () => getPlatformSupportTicket(viewChat!.id),
    enabled: isPlatform && Boolean(viewChat),
    retry: false,
  });


  const filteredRows = useMemo(
    () => filterRows(rows, tab, session?.id ?? 0),
    [rows, tab, session?.id]
  );

  if (!isAdmin) {
    return (
      <Wrapper>
        <Typography variant="h3" gutterBottom>
          {pageTitle}
        </Typography>
        <Typography color="text.secondary" data-testid="support-inbox-forbidden">
          You don&apos;t have access to the support inbox.
        </Typography>
      </Wrapper>
    );
  }

  const handleClaim = (row: Chat) => {
    if (!session) return;
    assignMutation.mutate({ chatId: row.id, adminId: session.id });
  };

  const handleOpenReassign = (row: Chat) => {
    setReassignChat(row);
    setReassignAdminId(String(row.assignedAdminId ?? ''));
  };
  const handleCloseReassign = () => {
    setReassignChat(null);
    setReassignAdminId('');
  };
  const handleSubmitReassign = () => {
    if (!reassignChat) return;
    const adminId = Number(reassignAdminId);
    if (!Number.isFinite(adminId) || adminId <= 0) return;
    assignMutation.mutate(
      { chatId: reassignChat.id, adminId },
      { onSettled: handleCloseReassign }
    );
  };

  const handleSetStatus = (row: Chat, status: SupportStatus) => {
    statusMutation.mutate({ chatId: row.id, status });
  };

  const handleOpenThread = (row: Chat) => {
    if (isPlatform) {
      setViewChat(row);
      return;
    }
    navigate(`/dashboards/messenger?chatId=${row.id}`);
  };

  return (
    <>
      <Helmet>
        <title>{pageTitle} - AVWorkforce</title>
      </Helmet>
      <Wrapper>
        <Typography variant="h3" gutterBottom>
          {pageTitle}
        </Typography>

        <Card sx={{ mb: 3 }}>
          <Tabs
            value={tab}
            onChange={(_e, next) => setTab(next as TabKey)}
            variant="scrollable"
            textColor="primary"
            indicatorColor="primary"
            data-testid="support-status-tabs"
          >
            {TAB_DEFS.map((t) => (
              <Tab
                key={t.key}
                value={t.key}
                label={t.label}
                data-testid={`support-tab-${t.key}`}
              />
            ))}
          </Tabs>
        </Card>

        {isFetching && filteredRows.length === 0 ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Card}>
            <Table size="small" data-testid="support-table">
              <TableHead>
                <TableRow>
                  <TableCell>Subject</TableCell>
                  <TableCell>Contractor</TableCell>
                  <TableCell>Job/Shift</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Assigned admin</TableCell>
                  <TableCell>Last message</TableCell>
                  <TableCell>Age</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <Typography
                        color="text.secondary"
                        sx={{ textAlign: 'center', py: 3 }}
                      >
                        No threads in this status.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {filteredRows.map((row) => {
                  const requester = describeRequester(row);
                  const status = row.supportStatus ?? 'OPEN';
                  const age = row.updatedAt
                    ? moment(row.updatedAt).fromNow(true)
                    : '—';
                  const canClaim = !row.assignedAdminId;
                  return (
                    <TableRow key={row.id} hover>
                      <TableCell>
                        {row.supportSubject || `Thread #${row.id}`}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Avatar
                            src={requester.avatar}
                            sx={{ width: 28, height: 28 }}
                          />
                          <Typography variant="body2">
                            {requester.name}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        {row.jobId ? (
                          <Button
                            size="small"
                            onClick={() =>
                              navigate(
                                `/dashboards/jobTracker/positions/${row.jobId}`
                              )
                            }
                          >
                            Job {row.jobId}
                          </Button>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell>
                        <Label color="primary">{status}</Label>
                      </TableCell>
                      <TableCell>
                        {row.assignedAdminId ? (
                          <Typography variant="body2">
                            Admin #{row.assignedAdminId}
                          </Typography>
                        ) : (
                          <Typography
                            color="text.secondary"
                            variant="body2"
                          >
                            Unassigned
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                          {row.lastMessage ?? '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>{age}</TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          {canClaim && (
                            <Button
                              size="small"
                              startIcon={<PersonAdd />}
                              onClick={() => handleClaim(row)}
                              disabled={assignMutation.isLoading}
                              data-testid={`support-claim-${row.id}`}
                            >
                              Claim
                            </Button>
                          )}
                          <Button
                            size="small"
                            startIcon={<SwapHoriz />}
                            onClick={() => handleOpenReassign(row)}
                            data-testid={`support-reassign-${row.id}`}
                          >
                            Reassign
                          </Button>
                          <FormControl size="small" sx={{ minWidth: 140 }}>
                            <Select
                              value={status}
                              onChange={(e) =>
                                handleSetStatus(row, e.target.value as SupportStatus)
                              }
                              data-testid={`support-status-${row.id}`}
                            >
                              {STATUS_OPTIONS.map((s) => (
                                <MenuItem key={s} value={s}>
                                  {s}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <IconButton
                            size="small"
                            onClick={() => handleOpenThread(row)}
                            aria-label="Open thread"
                            data-testid={`support-open-${row.id}`}
                          >
                            <OpenInNew fontSize="small" />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Wrapper>

      <Dialog
        open={!!reassignChat}
        onClose={handleCloseReassign}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Reassign support thread</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter the user id of the admin to reassign to.
          </Typography>
          <TextField
            fullWidth
            label="Admin user id"
            value={reassignAdminId}
            onChange={(e) => setReassignAdminId(e.target.value)}
            type="number"
            data-testid="support-reassign-input"
          />
          <Box mt={1}>
            <Typography variant="caption" color="text.secondary">
              <ChatBubbleOutline fontSize="inherit" />
              &nbsp;The server validates that the target user has an admin
              role before applying the assignment.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseReassign}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmitReassign}
            disabled={!reassignAdminId || assignMutation.isLoading}
            data-testid="support-reassign-submit"
          >
            Reassign
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isPlatform && !!viewChat}
        onClose={() => setViewChat(null)}
        maxWidth="md"
        fullWidth
        aria-labelledby="platform-support-ticket-title"
      >
        <DialogTitle id="platform-support-ticket-title">
          {ticketDetail.data?.supportSubject ??
            viewChat?.supportSubject ??
            'Support ticket'}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              justifyContent="space-between"
            >
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Requester
                </Typography>
                <Typography variant="body2">
                  {viewChat ? describeRequester(viewChat).name : 'Unknown'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Category
                </Typography>
                <Typography variant="body2">
                  {ticketDetail.data?.supportCategory ??
                    viewChat?.supportCategory ??
                    'General support'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Status
                </Typography>
                <Typography variant="body2">
                  {ticketDetail.data?.supportStatus ??
                    viewChat?.supportStatus ??
                    'OPEN'}
                </Typography>
              </Box>
            </Stack>

            <Box sx={{ display: 'flex', flexDirection: 'column', height: '60vh', minHeight: 300 }}>
              {ticketDetail.isLoading && <CircularProgress size={28} />}
              {ticketDetail.isError && <Typography role="status">This ticket conversation is currently unavailable.</Typography>}
              {ticketDetail.data && <ChatPaneV2 chat={ticketDetail.data} supportScope="platform" />}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewChat(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default function SupportInbox() {
  useAuthSessionRevision();
  // Mount the socket provider so the support listener fires while the
  // inbox is in focus. Routing this view independently of `/messenger`
  // keeps the inbox table at the full page width.
  return (
    <ChatSocketProvider>
      <SupportInboxInner />
    </ChatSocketProvider>
  );
}
